// @ts-nocheck
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Edit,
  Copy,
  Trash2,
  CheckCircle,
  FileText,
  Settings,
  Hash,
  AlertTriangle,
  Scale,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { format } from "date-fns";
import { toast } from "sonner";
import { useConfirm } from "@/providers/ConfirmProvider";
import { useQcClassification } from "@/components/quality/qc-form/useQcClassification";

export default function QCTemplateView() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const { data: template, isLoading } = useQuery({
    ...trpc.qcTemplates.getById.queryOptions({ id }),
    enabled: !!id,
  });

  // Lịch sử phiên bản cùng mã + nhãn đối tượng áp dụng từ danh mục động
  const { data: versions = [] } = useQuery({
    ...trpc.qcTemplates.versions.queryOptions({ id }),
    enabled: !!id,
  });
  const qcClass = useQcClassification();

  const publishMutation = useMutation({
    ...trpc.qcTemplates.publish.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [["qcTemplates", "getById"], { input: { id } }],
      });
      queryClient.invalidateQueries({ queryKey: [["qcTemplates", "list"]] });
    },
  });

  const duplicateMutation = useMutation(trpc.qcTemplates.duplicate.mutationOptions());
  const deleteMutation = useMutation(trpc.qcTemplates.delete.mutationOptions());

  const handleDuplicate = async () => {
    try {
      const cloned: any = await duplicateMutation.mutateAsync({ id });
      toast.success(`Đã nhân bản thành ${cloned.code}`);
      await navigate({ to: `/qc-templates/${cloned.id}/edit` as any });
      queryClient.invalidateQueries({ queryKey: trpc.qcTemplates.list.queryKey() });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi nhân bản biểu mẫu QC");
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Xóa biểu mẫu QC "${template?.code}"?`,
      message:
        "Nếu biểu mẫu đã được sử dụng trong các phiếu IQC/IPQC/FQC trước đó, hệ thống sẽ chuyển sang trạng thái Lưu trữ (ARCHIVED) để bảo toàn lịch sử.",
      confirmText: "Xác nhận xóa",
      cancelText: "Hủy",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      const res: any = await deleteMutation.mutateAsync({ id });
      toast.success(res?._message || "Đã xóa biểu mẫu QC");
      await navigate({ to: "/qc-templates", replace: true });
      queryClient.removeQueries({ queryKey: trpc.qcTemplates.getById.queryKey({ id }) });
      queryClient.removeQueries({ queryKey: trpc.qcTemplates.versions.queryKey({ id }) });
      queryClient.invalidateQueries({ queryKey: trpc.qcTemplates.list.queryKey() });
    } catch (err: any) {
      toast.error(err?.message || "Không thể xóa biểu mẫu QC");
    }
  };

  const handlePublish = async () => {
    const ok = await confirm({
      title: "Phát hành phiên bản mẫu QC?",
      message:
        "Phát hành phiên bản này? Bản đang áp dụng sẽ chuyển sang Lưu trữ; các phiếu QC cũ vẫn giữ nguyên chỉ tiêu của mình.",
      confirmText: "Phát hành",
      cancelText: "Hủy",
    });
    if (!ok) return;
    try {
      await publishMutation.mutateAsync({ id });
      toast.success("Đã phát hành phiên bản mẫu QC thành công");
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi phát hành phiên bản mẫu QC");
    }
  };

  if (isLoading) return <div className="p-8 text-center">Đang tải...</div>;
  if (!template)
    return (
      <div className="p-8 text-center text-red-500">
        Không tìm thấy biểu mẫu.
      </div>
    );

  const breadcrumbs = [{ label: "Biểu mẫu QC", to: "/qc-templates" }];

  return (
    <StandardDetailLayout
      entityType="qc_template"
      entityId={template.id}
      title={`${template.code} v${template.version}`}
      breadcrumbs={breadcrumbs}
      status={
        <Badge
          variant={
            template.status === "ACTIVE"
              ? "default"
              : template.status === "ARCHIVED"
                ? "secondary"
                : "outline"
          }
        >
          {template.status === "ACTIVE" ? "Đang áp dụng" : template.status === "ARCHIVED" ? "Lưu trữ" : template.status === "DRAFT" ? "Nháp" : template.status}
        </Badge>
      }
      actions={
        <div className="flex items-center gap-2">
          <Link to={`/qc-templates/${template.id}/edit` as any}>
            <Button variant="default" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <Edit className="mr-1.5 h-3.5 w-3.5" /> Chỉnh sửa chỉ tiêu
            </Button>
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDuplicate}
            disabled={duplicateMutation.isPending}
          >
            <Copy className="mr-1.5 h-3.5 w-3.5" /> Nhân bản
          </Button>
          {template.status === "DRAFT" && (
            <Button
              size="sm"
              onClick={handlePublish}
              disabled={publishMutation.isPending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <CheckCircle className="mr-1.5 h-3.5 w-3.5" /> Phát hành
            </Button>
          )}
          {template.status === "ACTIVE" && (
            <Link to={`/qc-templates/create?cloneFrom=${template.id}` as any}>
              <Button variant="outline" size="sm" className="bg-slate-50">
                <Settings className="mr-1.5 h-3.5 w-3.5" /> Phiên bản mới
              </Button>
            </Link>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="text-rose-600 border-rose-200 hover:bg-rose-50"
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Xóa
          </Button>
        </div>
      }
      timestamps={{
        created: template.createdAt,
        updated: template.updatedAt,
      }}
    >

      <div>
        <div className="space-y-4">
          {/* Thông tin chung: phân hệ, đối tượng áp dụng, lấy mẫu & AQL (trước đây trang chỉ có checklist) */}
          {/* Thẻ thông tin chung dày, toàn chiều rộng — như trang Đơn hàng (nhãn trái, giá trị đậm) */}
          <Card data-testid="qc-template-general">
            <CardContent className="p-3">
              <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5 text-sm">
                {(() => {
                  const scope = template.type || template.scope;
                  const rows: [string, React.ReactNode][] = [
                    ["Mã mẫu", <span className="font-mono">{template.code}</span>],
                    ["Tên mẫu", template.name],
                    ["Phiên bản", `v${template.version}`],
                    ["Phân hệ", scope === "IQC" ? "IQC · Vật tư đầu vào" : scope === "IPQC" ? "IPQC · Công đoạn" : scope === "FQC" ? "FQC · Thành phẩm" : scope],
                  ];
                  if (scope === "IQC") rows.push(["Nhóm vật tư áp dụng", qcClass.materialLabel(template.materialCategory || template.materialType) || "Mọi nhóm"]);
                  if (scope === "IPQC") rows.push(["Công đoạn áp dụng", qcClass.stageLabel(template.processStage) || "Mọi công đoạn"]);
                  if (scope === "FQC") {
                    rows.push(["Nhóm thành phẩm", qcClass.productGroupLabel(template.productCategory) || "Mọi nhóm"]);
                    rows.push(["Loại sản phẩm áp dụng", template.productType?.name || template.productType?.category || template.productType?.code || "Mọi loại trong nhóm"]);
                  }
                  rows.push(["Quy tắc lấy mẫu", template.samplingRule || "—"]);
                  rows.push(["AQL nghiêm trọng / nặng / nhẹ", `${template.aqlCritical ?? 0} / ${template.aqlMajor ?? 1.5} / ${template.aqlMinor ?? 4}`]);
                  if (scope === "IPQC") {
                    rows.push(["Tần suất kiểm lặp lại", template.patrolFrequency || "—"]);
                    rows.push(["Số mẫu ký", template.setupSampleQty ?? "—"]);
                  }
                  if (template.description) rows.push(["Mô tả", template.description]);
                  return rows.map(([k, v]) => (
                    <div key={k} className="flex gap-2 min-w-0 items-start">
                      <dt className="text-xs text-slate-500 shrink-0 w-28 pt-[3px]">{k}</dt>
                      <dd className="font-semibold text-slate-800 min-w-0 break-words leading-snug">{v}</dd>
                    </div>
                  ));
                })()}
              </dl>
            </CardContent>
          </Card>

          {/* Lịch sử phiên bản cùng mã: bấm để mở từng bản */}
          <Card data-testid="qc-template-versions">
            <CardHeader className="bg-white pb-3">
              <CardTitle className="text-base">Lịch sử phiên bản ({(versions as any[]).length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="text-left font-semibold px-4 py-2">Phiên bản</th>
                    <th className="text-left font-semibold px-4 py-2">Trạng thái</th>
                    <th className="text-right font-semibold px-4 py-2">Số chỉ tiêu</th>
                    <th className="text-left font-semibold px-4 py-2">Cập nhật</th>
                  </tr>
                </thead>
                <tbody>
                  {(versions as any[]).map((v: any) => (
                    <tr
                      key={v.id}
                      data-testid="qc-template-version-row"
                      onClick={() => v.id !== template.id && navigate({ to: "/qc-templates/$id", params: { id: v.id } } as any)}
                      className={v.id === template.id ? "bg-indigo-50/60 font-semibold" : "cursor-pointer hover:bg-slate-50"}
                    >
                      <td className="px-4 py-2 font-mono">v{v.version}{v.id === template.id ? " · đang xem" : ""}</td>
                      <td className="px-4 py-2">
                        {v.status === "ACTIVE" ? "Đang áp dụng" : v.status === "DRAFT" ? "Nháp" : v.status === "ARCHIVED" ? "Lưu trữ" : v.status}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{v.criteriaCount}</td>
                      <td className="px-4 py-2 text-slate-500">{v.updatedAt ? format(new Date(v.updatedAt), "dd/MM/yyyy HH:mm") : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="bg-white">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-indigo-500" /> Tiêu chí kiểm
                tra ({template.criteria?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {(template.criteria as any[])?.map(
                  (criterion: any, index: number) => (
                    <div
                      key={criterion.id || index}
                      className="p-4 hover:bg-slate-50 flex items-start gap-4 transition-colors"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-900">
                            {criterion.name}
                          </span>
                          {criterion.required && (
                            <Badge
                              variant="outline"
                              className="text-red-500 border-red-200 bg-red-50 text-[10px] px-1 py-0 h-4"
                            >
                              Bắt buộc
                            </Badge>
                          )}
                          {criterion.category ? (
                            <Badge variant="outline" className="bg-white">
                              {criterion.category}
                            </Badge>
                          ) : null}
                          {criterion.repeat ? (
                            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                              Đo lại mỗi lần kiểm
                            </Badge>
                          ) : null}
                        </div>

                        {criterion.description && (
                          <p className="text-sm text-slate-500 mb-2">
                            {criterion.description}
                          </p>
                        )}

                        <div className="mt-3 flex items-center gap-4 text-sm">
                          {criterion.type === "QUANTITATIVE" && (
                            <div className="flex items-center gap-2 text-blue-700 bg-blue-50 px-3 py-1.5 rounded-md border border-blue-100">
                              <Hash className="h-4 w-4" />
                              <span>Định lượng</span>
                              <span className="text-blue-400">|</span>
                              <span>
                                Chuẩn:{" "}
                                <strong>
                                  {criterion.targetValue} {criterion.unit}
                                </strong>
                              </span>
                              <span className="text-blue-400">|</span>
                              <span>
                                Min–Max: [{criterion.minValue} -{" "}
                                {criterion.maxValue}] {criterion.unit}
                              </span>
                            </div>
                          )}

                          {criterion.type === "QUALITATIVE" && (
                            <div className="flex items-center gap-2 text-purple-700 bg-purple-50 px-3 py-1.5 rounded-md border border-purple-100">
                              <CheckCircle className="h-4 w-4" />
                              <span>Định tính</span>
                              <span className="text-purple-400">|</span>
                              <span>
                                {criterion.standard || criterion.options?.join(", ")}
                              </span>
                            </div>
                          )}

                          {criterion.type === "EVALUATION" && (
                            <div className="flex items-center gap-2 text-amber-700 bg-amber-50 px-3 py-1.5 rounded-md border border-amber-100">
                              <Scale className="h-4 w-4" />
                              <span>Đánh giá (Evaluation) / Điểm số</span>
                              <span className="text-amber-400">|</span>
                              <span>
                                Min: {criterion.minValue} - Max:{" "}
                                {criterion.maxValue}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ),
                )}
                {(!template.criteria || template.criteria.length === 0) && (
                  <div className="p-8 text-center text-slate-500 flex flex-col items-center justify-center">
                    <AlertTriangle className="h-8 w-8 text-amber-400 mb-2" />
                    Chưa có tiêu chí nào.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </StandardDetailLayout>
  );
}
